import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { GoogleMapsLoader } from './maps/GoogleMapsLoader';

const TopBanner = () => {
  const { language, t, isRTL } = useLanguage();
  const [currentDate, setCurrentDate] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [locationLabel, setLocationLabel] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [monoMode, setMonoMode] = useState(false);

  // Global mono mode (white/gray) toggle via "eye" icon
  useEffect(() => {
    try {
      setMonoMode(window.localStorage.getItem("a11y_mono_mode") === "enabled");
    } catch {
      setMonoMode(false);
    }
  }, []);

  useEffect(() => {
    try {
      const enabled = monoMode;
      if (enabled) {
        document.documentElement.classList.add("a11y-mono-mode");
        document.body.classList.add("a11y-mono-mode");
        window.localStorage.setItem("a11y_mono_mode", "enabled");
      } else {
        document.documentElement.classList.remove("a11y-mono-mode");
        document.body.classList.remove("a11y-mono-mode");
        window.localStorage.removeItem("a11y_mono_mode");
      }
    } catch {
      /* ignore */
    }
  }, [monoMode]);

  // Location: Geolocation + Google Maps Geocoder
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setLocationLoading(false);
      return;
    }
    const lang = language === 'ar' ? 'ar' : 'en';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        GoogleMapsLoader.load(lang, 'SA')
          .then((google) => {
            return new Promise<void>((resolve) => {
              let resolved = false;
              const done = () => {
                if (resolved) return;
                resolved = true;
                resolve();
              };
              const timeoutId = setTimeout(done, 10000);
              const geocoder = new google.maps.Geocoder();
              geocoder.geocode(
                { location: { lat, lng: lon } },
                (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
                  if (status === 'OK' && results?.[0]) {
                    const components = results[0].address_components || [];
                    const getByType = (type: string) =>
                      components.find((c) => c.types?.includes(type))?.long_name;
                    let city =
                      getByType('locality') ||
                      getByType('administrative_area_level_2') ||
                      getByType('administrative_area_level_1') ||
                      getByType('country');
                    if (!city && results[0].formatted_address)
                      city = results[0].formatted_address;
                    if (city) setLocationLabel(city);
                  }
                  clearTimeout(timeoutId);
                  done();
                }
              );
            });
          })
          .catch(() => {})
          .finally(() => setLocationLoading(false));
      },
      () => {
        setLocationLabel(null);
        setLocationLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 }
    );
  }, [language]);

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      
      if (language === 'ar') {
        // Format date as "3 - سبتمبر - 2024"
        const day = now.getDate();
        const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
        const month = monthNames[now.getMonth()];
        const year = now.getFullYear();
        const formattedDate = `${day} - ${month} - ${year}`;

        // Format time as "2:30 مساءً"
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const ampm = hours >= 12 ? 'مساءً' : 'صبحاً';
        const displayHours = hours % 12 || 12;
        const formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;

        setCurrentDate(formattedDate);
        setCurrentTime(formattedTime);
      } else {
        // English format
        const formattedDate = now.toLocaleDateString('en-US', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        });
        const formattedTime = now.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });

        setCurrentDate(formattedDate);
        setCurrentTime(formattedTime);
      }
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [language]);

  return (
    <div className="bg-[#166A45] text-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`flex flex-wrap items-center justify-between gap-y-2 px-4 md:px-6 py-2 text-sm ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Info block: Location, Date, Time, Weather (stack/wrap on small screens) */}
        <div className={`flex flex-wrap items-center gap-3 sm:gap-4 min-w-0 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Location */}
          <div className={`flex items-center gap-2 min-w-0 max-w-[180px] sm:max-w-none ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <svg className="flex-shrink-0 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span className="truncate">
              {locationLoading
                ? (language === 'ar' ? 'جاري تحديد الموقع...' : 'Getting location...')
                : (locationLabel ?? t('location'))}
            </span>
          </div>
          {/* Time */}
          <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            <span>{currentTime || (language === 'ar' ? 'جاري التحميل...' : 'Loading...')}</span>
          </div>
          {/* Date */}
          <div className={`flex items-center gap-2 min-w-0 max-w-[140px] sm:max-w-none ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <svg className="flex-shrink-0 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span className="truncate">{currentDate || (language === 'ar' ? 'جاري التحميل...' : 'Loading...')}</span>
          </div>
          {/* Weather - hidden on very small screens to avoid overflow */}
          <div className={`hidden sm:flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/>
            </svg>
            <span>{t('weather')}</span>
          </div>
        </div>

        {/* Utility icons - hide mic on smallest to save space */}
        <div className={`flex items-center gap-1 sm:gap-3 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
          <button
            type="button"
            onClick={() => setMonoMode((v) => !v)}
            className="p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
            aria-label={monoMode ? (language === 'ar' ? 'إيقاف وضع أبيض/رمادي' : 'Disable mono mode') : (language === 'ar' ? 'تفعيل وضع أبيض/رمادي' : 'Enable mono mode')}
            aria-pressed={monoMode}
            title={monoMode ? (language === 'ar' ? 'إيقاف وضع أبيض/رمادي' : 'Disable mono mode') : (language === 'ar' ? 'تفعيل وضع أبيض/رمادي' : 'Enable mono mode')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button className="hidden sm:flex p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 items-center justify-center hover:bg-white/20 rounded transition-colors" aria-label={language === 'ar' ? 'بحث صوتي' : 'Voice search'}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopBanner;
